import queryString from 'query-string';
import { fetchUtils } from 'ra-core';

export default (
  apiUrl,
  httpClient = fetchUtils.fetchJson,
  countHeader = 'X-Total-Count',
) => ({
  getList: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const rangeStart = (page - 1) * perPage;
    const rangeEnd = page * perPage - 1;

    const query = {
      ...fetchUtils.flattenObject(params.filter),
      _sort: field,
      _order: order,
      _start: (page - 1) * perPage,
      _end: page * perPage,
      ts: Math.round(Date.now() / 5000),

      // sort: JSON.stringify([field, order]),
      // range: JSON.stringify([rangeStart, rangeEnd]),
      // filter: JSON.stringify(params.filter),
    };
    const url = `${apiUrl}/${resource}?${queryString.stringify(query)}`;
    const options =
      countHeader === 'Content-Range'
        ? {
          // Chrome doesn't return `Content-Range` header if no `Range` is provided in the request.
          headers: new Headers({
            Range: `${resource}=${rangeStart}-${rangeEnd}`,
          }),
        }
        : {};

    return httpClient(url, options).then(({ headers, json }) => {
      if (!headers.has(countHeader)) {
        throw new Error(
          `The ${countHeader} header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare ${countHeader} in the Access-Control-Expose-Headers header?`,
        );
      }
      return {
        data: json.data,
        total:
          countHeader === 'Content-Range'
            ? parseInt(headers.get('content-range').split('/').pop(), 10) :
            parseInt(headers.get(countHeader.toLowerCase()), 10),
      };
    });
  },

  getOne: (resource, params) => httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
    data: json.data,
  })),

  getMany: (resource, params) => {
    const query = {
      // filter: JSON.stringify({ id: params.ids }),
      ...fetchUtils.flattenObject(params.filter),
      _ids: params.ids.join(','),
    };
    const url = `${apiUrl}/${resource}?${queryString.stringify(query)}`;
    return httpClient(url).then(({ json }) => ({ data: json.data }));
  },

  getManyReference: (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const rangeStart = (page - 1) * perPage;
    const rangeEnd = page * perPage - 1;
    const query = {
      ...fetchUtils.flattenObject(params.filter),
      [params.target]: params.id,
      _sort: field,
      _order: order,
      _start: (page - 1) * perPage,
      _end: page * perPage,
      ts: Math.round(Date.now() / 5000),
      // sort: JSON.stringify([field, order]),
      // range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      // filter: JSON.stringify({
      //   ...params.filter,
      //   [params.target]: params.id,
      // }),
    };
    const url = `${apiUrl}/${resource}?${queryString.stringify(query)}`;
    const options =
      countHeader === 'Content-Range'
        ? {
          // Chrome doesn't return `Content-Range` header if no `Range` is provided in the request.
          headers: new Headers({
            Range: `${resource}=${rangeStart}-${rangeEnd}`,
          }),
        }
        : {};

    return httpClient(url, options).then(({ headers, json }) => {
      if (!headers.has(countHeader)) {
        throw new Error(
          `The ${countHeader} header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare ${countHeader} in the Access-Control-Expose-Headers header?`,
        );
      }
      return {
        data: json.data,
        total:
          countHeader === 'Content-Range'
            ? parseInt(
              headers.get('content-range').split('/').pop(),
              10,
            )
            : parseInt(headers.get(countHeader.toLowerCase()), 10),
      };
    });
  },

  update: (resource, params) => {
    const NULL_PERMITTED = ['preferredPaymentGateway', 'adSenseId'];
    const filteredData = Object.keys(params.data)
      .filter((field) => params.data[field] !== params.previousData[field])
      .filter((field) => params.data[field] !== null || NULL_PERMITTED.includes(field))
      .filter((field) => !['createdAt', 'updatedAt', 'id'].includes(field))
      .filter((field) => JSON.stringify(params.data[field]) !== JSON.stringify(params.previousData[field]))
      .reduce((all, field) => ({ ...all, [field]: params.data[field] }), {});
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'PUT',
      body: JSON.stringify(filteredData),
    }).then(({ json }) => ({ data: json.data }));
  },

  // simple-rest doesn't handle provide an updateMany route, so we fallback to calling update n times instead
  updateMany: (resource, params) => Promise.all(
    params.ids.map((id) => httpClient(`${apiUrl}/${resource}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    })),
  ).then((responses) => ({ data: responses.map(({ json }) => json.data.id) })),

  create: (resource, params) => httpClient(`${apiUrl}/${resource}`, {
    method: 'POST',
    body: JSON.stringify(params.data),
  }).then(({ json }) => ({
    data: { ...params.data, id: json.data.id },
  })),

  delete: (resource, params) => httpClient(`${apiUrl}/${resource}/${params.id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'text/plain',
    },
  }).then(({ json }) => ({ data: json.data })),

  // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
  deleteMany: (resource, params) => Promise.all(
    params.ids.map((id) => httpClient(`${apiUrl}/${resource}/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'text/plain',
      },
    })),
  ).then((responses) => ({
    data: responses.map(({ json }) => (json?.data && json.data.id)),
  })),
});
